import { Home, Thermostat, Vehicle, VehicleCharger } from '@/types'
import { Device, DeviceSelection } from '../DeviceSelectionGallery.types'
import { useDeviceAlertStates } from './useDeviceAlertStates'
import { useSelectionGalleryResources } from './useSelectionGalleryResources'
import { sortDevices } from './sortDevices'
import { useEffect, useMemo } from 'react'
import { getPiped } from '@/utils/function'
import { useGalleryActions } from './useGalleryActions'
import { EnergySystem } from '@/types/energySystem'
import { useBestDeviceForAutoSelect } from './useBestDeviceForAutoSelect'

type Props = {
  vehicles: Vehicle[]
  homes: Home[]
  thermostats: Thermostat[]
  vehicleChargers: VehicleCharger[]
  energySystems: EnergySystem[]
  selectedDevice: DeviceSelection | null
  setSelectedDevice: (device: DeviceSelection) => void
}

// function to turn all the devices in props to the Device type
function mapDevicesToType({
  vehicles,
  homes,
  thermostats,
  vehicleChargers,
  energySystems,
}: Props): Device[] {
  return [
    ...vehicles.map((vehicle) => ({
      type: 'vehicle' as const,
      id: vehicle.id,
      model: vehicle,
    })),
    ...homes.map((home) => ({
      type: 'home' as const,
      id: home.id,
      model: home,
    })),
    ...thermostats.map((thermostat) => ({
      type: 'thermostat' as const,
      id: thermostat.id,
      model: thermostat,
    })),
    ...vehicleChargers.map((vehicleCharger) => ({
      type: 'vehicleCharger' as const,
      id: vehicleCharger.id,
      model: vehicleCharger,
    })),
    ...energySystems.map((energySystem) => ({
      type: 'energySystem' as const,
      id: energySystem.id,
      model: energySystem,
    })),
  ]
}

export function useViewModel(props: Props) {
  const useTransformAndSortDevices = getPiped(
    mapDevicesToType,
    (devices) => useDeviceAlertStates({ devices }),
    (devicesWithAlertState) =>
      useSelectionGalleryResources({
        devices: devicesWithAlertState,
        selectedDevice: props.selectedDevice,
        setSelectedDevice: props.setSelectedDevice,
      }),
    sortDevices,
  )

  const sortedDevices = useTransformAndSortDevices(props)

  const galleryActions = useGalleryActions()

  const rawDevices = useMemo(
    () => mapDevicesToType(props),
    [
      props.vehicles,
      props.homes,
      props.thermostats,
      props.vehicleChargers,
      props.energySystems,
    ],
  )
  const { bestDevice, isProgramsLoading, hasAvailablePrograms } =
    useBestDeviceForAutoSelect(rawDevices)

  useEffect(() => {
    if (props.selectedDevice || sortedDevices.length === 0) {
      return
    }
    if (isProgramsLoading) {
      return
    }

    if (bestDevice) {
      props.setSelectedDevice(bestDevice)
    } else if (!hasAvailablePrograms) {
      props.setSelectedDevice(sortedDevices[0])
    }
  }, [
    sortedDevices.length,
    bestDevice,
    isProgramsLoading,
    hasAvailablePrograms,
  ])

  return {
    sortedDevices: sortedDevices,
    galleryActions,
  }
}
